/* eslint-disable */
import { calculateBillCharges } from '../utils/billCharges';
import React, {
  useState, useEffect, useRef, ReactChild,
} from 'react';
import {
  type DocumentReference,
  type DocumentSnapshot,
  type QuerySnapshot,
} from 'firebase/firestore';
import { functions, type HttpsCallableResult } from 'firebaseApp';
import currency from "currency.js";
import { getVenue } from 'firebase/venueFirestore';
import {
  observeComanda,
  observeComandaItems,
} from 'firebase/comandaFirestore';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  addItemToPendingOrder as addItemToPendingOrderAction, 
  removeItemFromPendingOrder as removePendingOrderAction, 
  updateItemInPendingOrder as updatePendingOrderAction,
  cleanPendingOrder as cleanPendingOrderAction,
  selectPendingOrders, 
  setDeliveryData
} from '../redux/slices/bills.slice';
import { resetDeliveryAddress, restoreDeliveryState, selectDeliveryAddress, selectFreightAmount, selectIsCalculatingFreight } from '../redux/slices/user.slice';
interface ComandaContextProps {
  isReady: boolean;
  billType?: Pagcomanda.BillType;
  billId?: Pagcomanda.BillId;
  comanda?: Pagcomanda.Comanda | null;
  comandaRef?: DocumentReference<Pagcomanda.Comanda>;
  comandaItems?: QuerySnapshot<Pagcomanda.ComandaItem>;
  venue?: DocumentSnapshot<Pagcomanda.Venue>;
  venueId?: string;
  pendingOrder?: Array<Pagcomanda.ComandaItem> | undefined;
  totalComanda: currency;
  totalPaid: currency;
  isDisposable?: boolean;
  metadata?: Pagcomanda.ComandaMetadata;
  cleanPendingOrder: () => void;
  addItemToPendingOrder: (item: Pagcomanda.ComandaItem) => void;
  removeItemFromPendingOrder: (index: number) => void;
  updateItemInPendingOrder: (index: number, item: Pagcomanda.ComandaItem) => void;
  submitOrder: SubmitOrderFunction;
}

type SubmitOrderFunction = (items?: Array<Pagcomanda.ComandaItem>, metadata?: {tableNumber?: string; metadata?: Pagcomanda.ComandaMetadata; tip?: Pagcomanda.TipSelection } | undefined) => Promise<AddItemToComandaResult>;

interface ComandaProviderProps {
  children: ReactChild;
  value?: {
    billType?: Pagcomanda.BillType;
    billId?: Pagcomanda.BillId;
    venueId?: Pagcomanda.BillId;
  }
}

export interface AddItemToComanda {
  Success?: boolean;
  BillType?: Pagcomanda.BillType;
  BillId?: Pagcomanda.BillId;
  NewBillId?: string;
  newBillId?: string;
  error?: {
    message?: string;
  };
}

interface AddItemToComandaPayload {
  BillType: Pagcomanda.BillType | undefined;
  BillId: Pagcomanda.BillId | undefined;
  VenueId: string | undefined;
  Items: Pagcomanda.ComandaItem[];
  Fulfillment?: Pagcomanda.BillFulfillment;
  Tip?: Pagcomanda.TipSelection;
  Metadata: object;
}

type AddItemToComandaResult = HttpsCallableResult<AddItemToComanda>;

const ComandaContext = React.createContext<ComandaContextProps>({
  isReady: false,
  totalComanda: currency(0),
  totalPaid: currency(0),
  cleanPendingOrder: () => null,
  addItemToPendingOrder: (item: Pagcomanda.ComandaItem) => null,
  removeItemFromPendingOrder: (index: number) => null,
  updateItemInPendingOrder: (index: number, item: Pagcomanda.ComandaItem) => null,
  submitOrder: (items: Array<Pagcomanda.ComandaItem> | undefined, metadata?: {tableNumber?: string;} | undefined) => new Promise<AddItemToComandaResult>((resolve) => resolve({} as AddItemToComandaResult)),
});

function ComandaProvider({ children, value }: ComandaProviderProps) {
  const dbSnapRef = useRef<() => void>();
  const dbItemsRef = useRef<() => void>();
  const {billType, billId, venueId} = value ?? {};

  const dispatch = useAppDispatch();

  const [isReady, setIsReady] = useState<boolean>(false);
  const [comanda, setComanda] = useState<Pagcomanda.Comanda | null>();
  const [comandaRef, setComandaRef] = useState<DocumentReference<Pagcomanda.Comanda>>();
  const [comandaItems, setComandaItems] = useState<QuerySnapshot<Pagcomanda.ComandaItem>>();
  const [venue, setVenue] = useState<DocumentSnapshot<Pagcomanda.Venue>>();

  const pendingOrdersFromRedux = useAppSelector(selectPendingOrders);
  const freightAmount = useAppSelector(selectFreightAmount);
  const calculatingFreight = useAppSelector(selectIsCalculatingFreight);
  const deliveryAddress = useAppSelector(selectDeliveryAddress);

  useEffect(() => {
    setIsReady(false);
    if (typeof dbSnapRef?.current === 'function') {
      dbSnapRef.current();
      dbSnapRef.current = undefined;
    }
    if (typeof dbItemsRef?.current === 'function') {
      dbItemsRef.current();
      dbItemsRef.current = undefined;
    }
    console.log("Unmount comanda.");
  }, []);

  useEffect(() => {
    if (!venueId) return;

    getVenue(venueId).then(setVenue);
  }, [venueId]);

  useEffect(() => {
    if (dbSnapRef.current || dbItemsRef.current) return;

    if (!billId) return;

    console.log("Mount comanda.", billType, billId);

    dbSnapRef.current = observeComanda(billType, billId, (comandaDoc) => {
      if (!comandaDoc.exists()) {
        setComanda(null);
        return;
      };
      const doc = comandaDoc.data();
      setComanda(doc);
      dispatch(setDeliveryData({
        billId: comandaDoc.id,
        metadata: (doc?.metadata ?? {}) as Pagcomanda.ComandaMetadata
      }))
      if (doc?.fulfillment?.type === 'delivery'
        && doc.fulfillment.address?.id
        && doc.fulfillment.address.data) {
        dispatch(restoreDeliveryState({
          address: {
            id: doc.fulfillment.address.id,
            placeId: doc.fulfillment.address.placeId,
            data: doc.fulfillment.address.data,
          },
          fee: doc.fulfillment.deliveryFee,
        }));
      } else {
        dispatch(resetDeliveryAddress());
      }
      setComandaRef(comandaDoc.ref);
      if (doc?.venue) {
        const venueIdFromRef = doc.venue.path.split('/')[1];
        getVenue(venueIdFromRef).then(setVenue);
      }
    });

    dbItemsRef.current = observeComandaItems(billType, billId, (snapshot) => {
      setComandaItems(snapshot);
    });
  }, [billType, billId]);

  useEffect(() => {
    if (
      comanda !== undefined
      && comandaItems !== undefined
      && venue !== undefined
    ) {
      setIsReady(true);
    }
  }, [comanda, comandaItems, venue]);

  useEffect(() => () => {
    if (typeof dbSnapRef?.current === 'function') dbSnapRef.current();
    if (typeof dbItemsRef?.current === 'function') dbItemsRef.current();
    dispatch(resetDeliveryAddress());
  }, []);

  let totalComanda = currency(0, { symbol: 'R$', decimal: ',', separator: '.' });
  let totalPaid = currency(0, { symbol: 'R$', decimal: ',', separator: '.' });

  if (comandaItems && comandaItems?.size > 0) {
    comandaItems.docs.forEach((doc) => {
      const item = doc.data();
      if (comanda?.fulfillment && item.productId === 'CALCULATED_DELIVERY') return;

      const price = currency(item.price);
      if (item.price >= 0 && !item?.isCanceled) {
          totalComanda = totalComanda.add(price.add(item?.additionalPrice ?? 0).multiply(item.qty));
      } else if (!item?.isCanceled) {
        totalPaid = totalPaid.add(price.add(item?.additionalPrice ?? 0).multiply(item.qty));
      }
    });
  }

  totalComanda = totalComanda.add(calculateBillCharges(totalComanda.value, comanda?.charges).total);

  if (comanda?.fulfillment?.type === 'delivery') {
    totalComanda = totalComanda.add(comanda.fulfillment.deliveryFee);
  }

  const addItemToPendingOrder = (item: Pagcomanda.ComandaItem) => {
    dispatch(addItemToPendingOrderAction({
      orderId: billId,
      item,
    }));
  };

  const removeItemFromPendingOrder = (index: number) => {
    dispatch(removePendingOrderAction({
      orderId: billId,
      itemIndex: index,
    }));
  };

  const updateItemInPendingOrder = (index: number, item: Pagcomanda.ComandaItem) => {
    dispatch(updatePendingOrderAction({
      orderId: billId,
      itemIndex: index,
      item,
    }));
  };

  const cleanPendingOrder = () => {
    dispatch(cleanPendingOrderAction({ orderId: billId }));
  };

  const submitOrder: SubmitOrderFunction = (items, data) => {
    const Items = [ ...(items || pendingOrdersFromRedux?.[billId || 'default'] || []) ];
    const {tableNumber, metadata, tip} = data ?? {};

    if (tableNumber) {
      Items?.forEach((item, key) => {
        Items[key]['tableNumber'] = tableNumber;
      });
    }

    const finalMetadata = {...metadata};
    const storedAddress = comanda?.fulfillment?.address;
    const selectedAddress = deliveryAddress ?? (
      storedAddress?.id && storedAddress.data
        ? { id: storedAddress.id, data: storedAddress.data }
        : null
    );
    const formattedAddress = selectedAddress
      ? [
        selectedAddress.data.street,
        selectedAddress.data.number,
        selectedAddress.data.complement,
        selectedAddress.data.district,
        selectedAddress.data.cityName,
        selectedAddress.data.stateInitials,
        selectedAddress.data.countryName,
      ].filter((i) => !!i).join(', ')
      : storedAddress?.formattedAddress
        ?? String(metadata?.['customerAddress']?.value ?? '');
    const orderTypeValue = metadata?.['orderType']?.value;
    const orderType = ['delivery', 'dinein', 'pickup'].includes(String(orderTypeValue))
      ? orderTypeValue as Pagcomanda.OrderType
      : undefined;
    const deliveryFee = calculatingFreight === 'succeeded'
      ? freightAmount
      : comanda?.fulfillment?.deliveryFee ?? 0;
    const fulfillment: Pagcomanda.BillFulfillment | undefined = orderType
      ? {
        type: orderType,
        deliveryFee: orderType === 'delivery' ? deliveryFee : 0,
        address: orderType === 'delivery'
          ? {
            id: selectedAddress?.id,
            placeId: selectedAddress?.placeId,
            data: selectedAddress?.data,
            formattedAddress,
          }
          : null,
      }
      : undefined;

    const fnAddItemToComanda = functions.httpsCallable<AddItemToComandaPayload, AddItemToComanda>('addItemToComanda');

    if (orderType === 'delivery') {
      finalMetadata['orderOrigin'] = {
        label: 'Origem do Pedido',
        value: 'webapp',
        description: 'SITE'
      };
      finalMetadata['customerAddress'] = {
        label: 'Endereço do cliente',
        value: formattedAddress,
        description: formattedAddress,
      };
    }

    return fnAddItemToComanda({
      BillType: billType,
      BillId: billId,
      VenueId: venue?.id,
      Items,
      ...(fulfillment && { Fulfillment: fulfillment }),
      Metadata: finalMetadata,
      ...(tip && { Tip: tip }),
    })
      .then<AddItemToComandaResult>((data) => {
        if (data?.data?.Success === true || data?.data?.newBillId) {
          cleanPendingOrder();
          return data as AddItemToComandaResult;
        }
        throw new Error(data?.data?.error?.message ?? 'Erro desconhecido.');
      })
      .catch((err) => {
        console.error(err);
        throw err;
      });
  };

  const finalValue: ComandaContextProps = {
    isReady,
    billType: comanda?.billType ?? billType,
    billId,
    comanda,
    comandaRef,
    comandaItems,
    venue,
    venueId: venue?.id,
    pendingOrder: pendingOrdersFromRedux?.[billId || 'default'] || [],
    totalComanda,
    totalPaid,
    metadata: comanda?.metadata ?? {},
    cleanPendingOrder,
    addItemToPendingOrder,
    removeItemFromPendingOrder,
    updateItemInPendingOrder,
    submitOrder,
  };

  return (
    <ComandaContext.Provider value={finalValue}>{children}</ComandaContext.Provider>
  );
}

export default ComandaContext;

export { ComandaProvider };
